/**
 * Obscura Eyewear — theme.js
 * Full production JavaScript for all interactive theme features.
 */

'use strict';

/* =========================================================
   CONSTANTS & STATE
   ========================================================= */
const FREE_SHIPPING_THRESHOLD = 7500; // pence (£75)

let cartData = null;

/* =========================================================
   UTILITY HELPERS
   ========================================================= */
function formatMoney(cents) {
  const pounds = (cents / 100).toFixed(2);
  return '£' + pounds;
}

function qs(selector, scope = document) {
  return scope.querySelector(selector);
}

function qsa(selector, scope = document) {
  return [...scope.querySelectorAll(selector)];
}

function on(el, event, handler, options) {
  if (el) el.addEventListener(event, handler, options);
}

/* =========================================================
   STICKY HEADER
   ========================================================= */
(function initStickyHeader() {
  const header = qs('.site-header');
  if (!header) return;

  let lastScroll = 0;
  let ticking = false;

  function handleScroll() {
    const current = window.scrollY;

    if (current > 80) {
      header.classList.add('is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
    }

    if (current > lastScroll && current > 300) {
      header.classList.add('is-hidden');
    } else {
      header.classList.remove('is-hidden');
    }

    lastScroll = current <= 0 ? 0 : current;
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(handleScroll);
      ticking = true;
    }
  }, { passive: true });
})();

/* =========================================================
   OVERLAY HELPER
   ========================================================= */
const Overlay = {
  el: null,

  init() {
    this.el = qs('#Overlay');
    if (!this.el) return;
    on(this.el, 'click', () => this.hide());
  },

  show() {
    if (!this.el) return;
    this.el.classList.add('is-visible');
    document.body.classList.add('overlay-open');
  },

  hide() {
    if (!this.el) return;
    this.el.classList.remove('is-visible');
    document.body.classList.remove('overlay-open');
    MobileMenu.close();
    SearchOverlay.close();
    CartDrawer.close();
  }
};

/* =========================================================
   MOBILE MENU
   ========================================================= */
const MobileMenu = {
  el: null,

  init() {
    this.el = qs('#MobileMenu');
    if (!this.el) return;

    qsa('[data-mobile-menu-open]').forEach(btn => {
      on(btn, 'click', () => this.open());
    });

    qsa('[data-mobile-menu-close]').forEach(btn => {
      on(btn, 'click', () => this.close());
    });
  },

  open() {
    if (!this.el) return;
    this.el.classList.add('is-open');
    // Show overlay specifically for mobile menu
    const overlay = document.querySelector('.mobile-menu__overlay');
    if (overlay) overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    document.querySelector('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'true');
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    const overlay = document.querySelector('.mobile-menu__overlay');
    if (overlay) overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    document.querySelector('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'false');
  },

  initAccordions() {
    qsa('[data-accordion-toggle]').forEach(btn => {
      on(btn, 'click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        const sub = btn.nextElementSibling;
        if (sub) {
          sub.classList.toggle('is-open', !expanded);
          sub.setAttribute('aria-hidden', expanded);
        }
      });
    });
  }
};

/* =========================================================
   SEARCH OVERLAY
   ========================================================= */
const SearchOverlay = {
  el: null,
  input: null,

  init() {
    this.el = qs('#SearchOverlay');
    if (!this.el) return;
    this.input = qs('input[type="search"]', this.el);

    qsa('[data-search-open]').forEach(btn => {
      on(btn, 'click', () => this.open());
    });

    qsa('[data-search-close]').forEach(btn => {
      on(btn, 'click', () => this.close());
    });

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  },

  open() {
    if (!this.el) return;
    this.el.classList.add('is-open');
    Overlay.show();
    document.body.style.overflow = 'hidden';
    if (this.input) setTimeout(() => this.input.focus(), 100);
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    document.body.style.overflow = '';
  }
};

/* =========================================================
   FREE SHIPPING BAR
   ========================================================= */
function updateShippingBar(totalCents) {
  const fill = qs('#CartShippingFill');
  const text = qs('#CartShippingText');
  if (!fill || !text) return;

  const remaining = FREE_SHIPPING_THRESHOLD - totalCents;

  if (remaining <= 0) {
    fill.style.width = '100%';
    fill.classList.add('cart-shipping__fill--done');
    text.textContent = "You've unlocked free UK shipping!";
  } else {
    const pct = Math.min((totalCents / FREE_SHIPPING_THRESHOLD) * 100, 100);
    fill.style.width = pct + '%';
    fill.classList.remove('cart-shipping__fill--done');
    text.textContent = `Add ${formatMoney(remaining)} more for free UK shipping`;
  }
}

/* =========================================================
   RENDER CART HTML
   ========================================================= */
function renderCart(cart) {
  cartData = cart;

  // Update badges
  qsa('.cart-count-badge, .cart-badge').forEach(badge => {
    badge.textContent = cart.item_count;
    badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
  });

  updateShippingBar(cart.total_price);

  const body = qs('#CartDrawerBody');
  if (!body) return;

  if (cart.items.length === 0) {
    body.innerHTML = `
      <div class="cart-empty">
        <svg class="cart-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 0 1-8 0"/>
        </svg>
        <p class="cart-empty__title">Your bag is empty</p>
        <p class="cart-empty__sub">Add some eyewear to get started</p>
        <button class="cart-continue" data-cart-close>Continue Shopping</button>
      </div>`;

    qsa('[data-cart-close]', body).forEach(btn => {
      on(btn, 'click', () => CartDrawer.close());
    });

    const footer = qs('#CartDrawerFooter');
    if (footer) footer.style.display = 'none';
    return;
  }

  const footer = qs('#CartDrawerFooter');
  if (footer) footer.style.display = '';

  // Original total (compare prices)
  let originalTotal = 0;
  cart.items.forEach(item => {
    originalTotal += (item.original_line_price || item.final_line_price);
  });
  const savings = originalTotal - cart.total_price;

  body.innerHTML = cart.items.map((item, index) => `
    <div class="cart-item" data-index="${index + 1}" data-key="${item.key}">
      <div class="cart-item__img-wrap">
        <img
          class="cart-item__img"
          src="${item.image ? item.image.replace('.jpg', '_120x120.jpg').replace('.png', '_120x120.png').replace('.webp', '_120x120.webp') : ''}"
          alt="${escapeHtml(item.title)}"
          width="80"
          height="80"
          loading="lazy"
        />
      </div>
      <div class="cart-item__info">
        <p class="cart-item__name">${escapeHtml(item.product_title)}</p>
        ${item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
          : ''}
        <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
        <div class="cart-item__controls">
          <div class="cart-qty">
            <button class="cart-qty__btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
            <span class="cart-qty__val">${item.quantity}</span>
            <button class="cart-qty__btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove item">Remove</button>
        </div>
      </div>
    </div>
  `).join('');

  // Bind qty/remove buttons
  qsa('.cart-qty__btn', body).forEach(btn => {
    on(btn, 'click', () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const newQty = action === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
      changeQty(key, newQty);
    });
  });

  qsa('.cart-item__remove', body).forEach(btn => {
    on(btn, 'click', () => {
      changeQty(btn.dataset.key, 0);
    });
  });

  // Update footer
  const subtotalEl = qs('.cart-total__amount');
  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  const savingsBar = qs('.cart-savings-bar');
  if (savingsBar) {
    if (savings > 0) {
      savingsBar.style.display = '';
      savingsBar.textContent = `You're saving ${formatMoney(savings)}`;
    } else {
      savingsBar.style.display = 'none';
    }
  }
}

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/* =========================================================
   AJAX CART API
   ========================================================= */
async function fetchCart() {
  const res = await fetch('/cart.js');
  const cart = await res.json();
  renderCart(cart);
  return cart;
}

async function addToCart(variantId, quantity = 1, properties = {}) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity, properties })
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.description || 'Could not add item to cart');
  }

  return res.json();
}

async function changeQty(key, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  const cart = await res.json();
  renderCart(cart);
  return cart;
}

/* =========================================================
   CART DRAWER
   ========================================================= */
const CartDrawer = {
  el: null,

  init() {
    this.el = qs('#CartDrawer');
    if (!this.el) return;

    qsa('[data-cart-open]').forEach(btn => {
      on(btn, 'click', () => this.open());
    });

    qsa('[data-cart-close]').forEach(btn => {
      on(btn, 'click', () => this.close());
    });

    on(document, 'keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    fetchCart();
  },

  async open() {
    if (!this.el) return;
    await fetchCart();
    this.el.classList.add('is-open');
    Overlay.show();
    document.body.style.overflow = 'hidden';
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    document.body.style.overflow = '';
    Overlay.hide();
  }
};

/* =========================================================
   ADD TO CART — intercept product forms & quick-add buttons
   ========================================================= */
async function handleAddToCart(btn, variantId) {
  if (!variantId) return;

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding…';

  try {
    await addToCart(variantId, 1);
    await fetchCart();
    btn.textContent = 'Added!';
    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    }, 1500);
    CartDrawer.open();
  } catch (err) {
    btn.textContent = err.message || 'Error';
    btn.disabled = false;
    console.error('[Obscura] Add to cart error:', err);
    setTimeout(() => {
      btn.textContent = originalText;
    }, 2000);
  }
}

function initAddToCart() {
  // Intercept product form submit
  on(document, 'submit', async (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    const variantInput = qs('[name="id"]', form);
    if (!variantInput) return;

    const btn = qs('[type="submit"]', form) || qs('[data-add-to-cart]', form);
    await handleAddToCart(btn, variantInput.value);
  });

  // Quick-add buttons (data-product-id = variant id)
  on(document, 'click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart][data-product-id]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.productId;
    await handleAddToCart(btn, variantId);
  });
}

/* =========================================================
   PRODUCT GALLERY THUMBNAILS
   ========================================================= */
function initProductGallery() {
  const mainImg = qs('.product-gallery__main img');
  const thumbs = qsa('.product-gallery__thumbs .thumb');

  if (!mainImg || thumbs.length === 0) return;

  thumbs.forEach(thumb => {
    on(thumb, 'click', () => {
      const src = thumb.dataset.src || thumb.querySelector('img')?.src;
      const srcset = thumb.dataset.srcset || '';
      if (src) {
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
      }
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* =========================================================
   VARIANT SELECTOR
   ========================================================= */
function initVariantSelector() {
  const variantBtns = qsa('[data-variant-btn]');
  const variantInput = qs('input[name="id"]');

  if (variantBtns.length === 0 || !variantInput) return;

  // Build selected options from initial is-active state (set by Liquid)
  function getSelectedOptions() {
    const selected = {};
    qsa('[data-variant-btn].is-active').forEach(btn => {
      selected[btn.dataset.optionGroup] = btn.dataset.optionValue;
    });
    return selected;
  }

  // Find and apply matching variant
  function applyVariant(selectedOptions) {
    const variants = window.__obscuraVariants || [];
    const match = variants.find(v =>
      v.options.every((opt, i) => {
        const key = `option${i + 1}`;
        return !selectedOptions[key] || v[key] === selectedOptions[key];
      })
    );
    if (!match) return;

    // Update hidden input
    variantInput.value = match.id;

    // Update PDP price
    const pdpPrice = qs('#PdpPrice');
    const pdpCompare = qs('#PdpCompare');
    const savePill = qs('.pdp__save-pill');
    if (pdpPrice) pdpPrice.textContent = formatMoney(match.price);
    if (pdpCompare) {
      if (match.compare_at_price > match.price) {
        pdpCompare.textContent = formatMoney(match.compare_at_price);
        pdpCompare.style.display = '';
        if (savePill) savePill.style.display = '';
      } else {
        pdpCompare.style.display = 'none';
        if (savePill) savePill.style.display = 'none';
      }
    }

    // Update sticky bar price
    const stickyPrice = qs('#StickyPrice');
    if (stickyPrice) stickyPrice.textContent = formatMoney(match.price);

    // Update ATC buttons (desktop + sticky)
    [qs('#AddToCart'), qs('#StickyAtcBtn')].forEach(btn => {
      if (!btn) return;
      btn.disabled = !match.available;
      if (btn.id === 'AddToCart') {
        btn.innerHTML = match.available
          ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="18" height="18" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>Add to Bag`
          : 'Sold Out';
      } else {
        btn.textContent = match.available ? 'Add to Bag' : 'Sold Out';
      }
    });

    // Update option value display labels
    qsa('.pdp__option-value').forEach((el, i) => {
      if (match[`option${i + 1}`]) el.textContent = match[`option${i + 1}`];
    });

    // Switch gallery image to variant's featured image
    if (match.featured_image && match.featured_image.src) {
      const src = match.featured_image.src;
      // Desktop main image
      const mainImg = qs('#PdpMainImg');
      if (mainImg) {
        mainImg.src = src.replace(/.(jpg|jpeg|png|webp)/i, '_800x.$1');
        mainImg.srcset = src.replace(/.(jpg|jpeg|png|webp)/i, '_480x.$1') + ' 480w, ' +
                         src.replace(/.(jpg|jpeg|png|webp)/i, '_800x.$1') + ' 800w';
      }
      // Desktop thumbnails — mark matching thumb as active
      qsa('[data-pdp-thumb]').forEach(btn => {
        const thumbSrc = btn.dataset.pdpThumb || '';
        const isSame = thumbSrc.split('?')[0].replace(/_\d+x/, '') ===
                       src.split('?')[0].replace(/_\d+x/, '');
        btn.classList.toggle('is-active', isSame);
      });
      // Mobile gallery — scroll to matching slide
      const track = qs('#PdpMobTrack');
      if (track) {
        const slides = Array.from(track.querySelectorAll('.pdp-mob-gallery__slide'));
        const idx = slides.findIndex(slide => {
          const img = slide.querySelector('img');
          return img && img.src.split('?')[0].replace(/_\d+x/, '') === src.split('?')[0].replace(/_\d+x/, '');
        });
        if (idx > -1) {
          slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          qsa('[data-dot]').forEach((d, i) => d.classList.toggle('is-active', i === idx));
        }
      }
    }

    // Filter gallery to show only images for the selected variant's group
    (function filterGalleryByVariant() {
      const allVariants = window.__obscuraVariants || [];
      if (allVariants.length <= 1) return; // Single variant — show all images

      const totalThumbs = qsa('[data-pdp-thumb]').length;
      const groupSize = totalThumbs / allVariants.length;
      if (!Number.isInteger(groupSize) || groupSize < 1) return; // Uneven — skip filtering

      const variantIdx = allVariants.findIndex(v => v.id === match.id);
      if (variantIdx < 0) return;

      const start = variantIdx * groupSize;
      const end = start + groupSize;

      // Desktop thumbnails
      qsa('[data-pdp-thumb]').forEach((btn, i) => {
        btn.style.display = (i >= start && i < end) ? '' : 'none';
      });

      // Mobile slides + dots
      const track = qs('#PdpMobTrack');
      const dots = qsa('[data-dot]');
      if (track) {
        Array.from(track.querySelectorAll('.pdp-mob-gallery__slide')).forEach((slide, i) => {
          slide.style.display = (i >= start && i < end) ? '' : 'none';
        });
        // Re-index visible dots
        let visIdx = 0;
        dots.forEach((dot, i) => {
          if (i >= start && i < end) {
            dot.style.display = '';
            dot.dataset.dot = visIdx++;
          } else {
            dot.style.display = 'none';
          }
        });
      }
    })();
  }

  // Handle variant button clicks
  variantBtns.forEach(btn => {
    on(btn, 'click', () => {
      const group = btn.dataset.optionGroup;
      // Update active state within group
      qsa(`[data-variant-btn][data-option-group="${group}"]`).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      // Apply matching variant
      applyVariant(getSelectedOptions());
    });
  });

  // Apply initial variant on page load
  applyVariant(getSelectedOptions());
}

/* =========================================================
   TAB SWITCHER
   ========================================================= */
function initTabs() {
  on(document, 'click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;

    const container = tab.closest('[data-tabs-container]') || document;
    const tabId = tab.dataset.tab;

    qsa('[data-tab]', container).forEach(t => t.classList.remove('is-active'));
    qsa('[data-tab-panel]', container).forEach(p => p.classList.remove('is-active'));

    tab.classList.add('is-active');
    const panel = qs(`[data-tab-panel="${tabId}"]`, container);
    if (panel) panel.classList.add('is-active');
  });
}

/* =========================================================
   FILTER ACCORDION
   ========================================================= */
function initFilterAccordion() {
  on(document, 'click', (e) => {
    const trigger = e.target.closest('[data-filter-toggle]');
    if (!trigger) return;

    const parent = trigger.closest('.filter-group');
    if (!parent) return;

    parent.classList.toggle('is-open');
    const body = qs('.filter-group__body', parent);
    if (body) {
      body.style.maxHeight = parent.classList.contains('is-open')
        ? body.scrollHeight + 'px'
        : '0';
    }
  });

  // Open all by default
  qsa('.filter-group').forEach(group => {
    group.classList.add('is-open');
    const body = qs('.filter-group__body', group);
    if (body) body.style.maxHeight = body.scrollHeight + 'px';
  });
}

/* =========================================================
   SCROLL REVEAL (IntersectionObserver)
   ========================================================= */
function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px'
  });

  qsa('[data-reveal]').forEach(el => observer.observe(el));
}

/* =========================================================
   COUNTDOWN TIMER
   ========================================================= */
function initCountdown() {
  const timers = qsa('[data-countdown]');
  if (timers.length === 0) return;

  timers.forEach(timer => {
    const target = new Date(timer.dataset.countdown).getTime();

    function tick() {
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        timer.innerHTML = '<span class="countdown__expired">Offer ended</span>';
        return;
      }

      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);

      timer.innerHTML = `
        <span class="countdown__unit"><span class="countdown__val">${String(hours).padStart(2,'0')}</span><span class="countdown__label">HRS</span></span>
        <span class="countdown__sep">:</span>
        <span class="countdown__unit"><span class="countdown__val">${String(mins).padStart(2,'0')}</span><span class="countdown__label">MIN</span></span>
        <span class="countdown__sep">:</span>
        <span class="countdown__unit"><span class="countdown__val">${String(secs).padStart(2,'0')}</span><span class="countdown__label">SEC</span></span>
      `;
    }

    tick();
    setInterval(tick, 1000);
  });
}

/* =========================================================
   QUANTITY SELECTOR (PDP)
   ========================================================= */
function initQtySelector() {
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-qty-btn]');
    if (!btn) return;

    const wrap = btn.closest('[data-qty-wrap]');
    if (!wrap) return;

    const input = qs('input[name="quantity"]', wrap) || qs('.qty-val', wrap);
    if (!input) return;

    const current = parseInt(input.value || input.textContent || '1', 10);
    const action = btn.dataset.qtyBtn;

    if (action === 'increase') {
      const newVal = current + 1;
      if (input.tagName === 'INPUT') input.value = newVal;
      else input.textContent = newVal;
    } else if (action === 'decrease' && current > 1) {
      const newVal = current - 1;
      if (input.tagName === 'INPUT') input.value = newVal;
      else input.textContent = newVal;
    }
  });
}

/* =========================================================
   WISHLIST (client-side toggle, no backend)
   ========================================================= */
function initWishlist() {
  const STORAGE_KEY = 'obscura_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function syncButtons() {
    const list = getWishlist();
    qsa('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.wishlistBtn;
      btn.classList.toggle('is-wishlisted', list.includes(id));
      btn.setAttribute('aria-pressed', list.includes(id));
    });
  }

  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    const id = btn.dataset.wishlistBtn;
    const list = getWishlist();
    const idx = list.indexOf(id);

    if (idx === -1) list.push(id);
    else list.splice(idx, 1);

    saveWishlist(list);
    syncButtons();
  });

  syncButtons();
}

/* =========================================================
   ANNOUNCEMENT TICKER — fallback if CSS animation fails
   ========================================================= */
function initTicker() {
  const ticker = qs('.ticker__track');
  if (!ticker) return;
  // CSS handles animation; JS clone for seamless loop
  const clone = ticker.cloneNode(true);
  clone.setAttribute('aria-hidden', 'true');
  ticker.parentElement.appendChild(clone);
}

/* =========================================================
   SORT DROPDOWN
   ========================================================= */
function initSortDropdown() {
  const sort = qs('[data-sort-select]');
  if (!sort) return;

  on(sort, 'change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    window.location.href = url.toString();
  });
}

/* =========================================================
   INIT ALL
   ========================================================= */
document.addEventListener('DOMContentLoaded', () => {
  Overlay.init();
  MobileMenu.init();
  MobileMenu.initAccordions();
  SearchOverlay.init();
  CartDrawer.init();
  initAddToCart();
  initProductGallery();
  initVariantSelector();
  initTabs();
  initFilterAccordion();
  initScrollReveal();
  initCountdown();
  initQtySelector();
  initWishlist();
  initTicker();
  initSortDropdown();
  initScrollTop();
});

/* =========================================================
   SCROLL TO TOP
   ========================================================= */
function initScrollTop() {
  const btn = qs('#ScrollTop');
  if (!btn) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > 400) {
      btn.classList.add('is-visible');
    } else {
      btn.classList.remove('is-visible');
    }
  }, { passive: true });

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}
